import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    IVehiculeDataService,
    VehiculeDataServiceName,
    VehiculeEndpoints,
} from '../../../DataServices/VehiculeDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { ApiDataResponse, BaseApiDataResponse } from '../../../Models/ApiData';
import {
    Vehicule,
    VehiculeDetailsApiDataResponse,
    VehiculeListOrder,
    VehiculeListResponse,
} from '../../../Models/Vehicules/Vehicule';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IVehiculeService {
    listVehicule: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: VehiculeListOrder;
            is_actif?: boolean;
        }
    ) => Promise<VehiculeListResponse>;

    getVehicule: (
        cts: XHRRequestCanceler,
        vehiculeId: string
    ) => Promise<VehiculeDetailsApiDataResponse>;

    updateVehicule: (
        vehiculeId: string,
        vehicule: Vehicule,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createVehicule: (
        vehicule: Vehicule,
        cts: XHRRequestCanceler
    ) => Promise<ApiDataResponse<string>>;

    exportVehiculeList: (
        cts: XHRRequestCanceler,
        search: string,
        order?: VehiculeListOrder,
        is_actif?: boolean
    ) => void;
}

class VehiculeService extends BaseReduxService implements IVehiculeService {
    private readonly vehiculeDataService: Lazy<IVehiculeDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.vehiculeDataService = IoC.getLazy<IVehiculeDataService>(VehiculeDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    public listVehicule = async (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: VehiculeListOrder;
            is_actif?: boolean;
        }
    ) => {
        try {
            return this.vehiculeDataService.value().listVehicule(
                {
                    search: params.search,
                    order: params.order,
                    is_actif: params.is_actif,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getVehicule = (cts: XHRRequestCanceler, vehiculeId: string) => {
        try {
            return this.vehiculeDataService.value().getVehicule(cts, vehiculeId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public updateVehicule = async (
        vehiculeId: string,
        vehicule: Vehicule,
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.vehiculeDataService
                .value()
                .updateVehicule(vehiculeId, vehicule, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createVehicule = async (
        vehicule: Vehicule,
        cts: XHRRequestCanceler
    ): Promise<ApiDataResponse<string>> => {
        try {
            const response = await this.vehiculeDataService.value().createVehicule(vehicule, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    public exportVehiculeList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: VehiculeListOrder | undefined,
        is_actif?: boolean
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            if (is_actif) {
                params['is_actif'] = is_actif.valueOf().toString();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: VehiculeEndpoints.EXPORT_VEHICULE_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Vehicules', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const VehiculeServiceName = 'VehiculeService';
export { VehiculeService, VehiculeServiceName };
